import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { restoreOrder } from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import { ORDER_STATUS_LABELS, orderTitle } from "@/lib/orderUtils";
import type { OrderDetail, OrderStatus, OrderSummary } from "@/lib/types";

// Status válidos para restaurar (etapas manuais; entrega/rota e cancelado ficam de fora).
const RESTORE_TARGETS: OrderStatus[] = ["CRIADO", "EM_COMPRA", "EM_PRODUCAO", "EM_EXPEDICAO"];

interface Props {
  order: OrderSummary | null;
  onClose: () => void;
  onRestored?: (order: OrderDetail) => void;
}

/** Janela para escolher para qual status um pedido cancelado deve voltar. */
export function RestoreOrderDialog({ order, onClose, onRestored }: Props) {
  const [target, setTarget] = useState<OrderStatus>("CRIADO");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTarget("CRIADO");
  }, [order?.id]);

  const submit = async () => {
    if (!order) return;
    setSaving(true);
    try {
      const updated = await restoreOrder(order.id, target);
      onRestored?.(updated);
      toast.success(`Pedido restaurado para ${ORDER_STATUS_LABELS[target]}`);
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível restaurar o pedido"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={order != null} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" /> Restaurar pedido
          </DialogTitle>
          <DialogDescription>{order ? orderTitle(order) : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          <label className="text-sm font-medium">Voltar para o status</label>
          <Select value={target} onValueChange={(v) => setTarget(v as OrderStatus)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESTORE_TARGETS.map((s) => (
                <SelectItem key={s} value={s}>
                  {ORDER_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Restaurar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
